import { useEffect, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table';
import { toast } from 'react-toastify';

const useCrudListPage = ({
  items,
  fetchItems,
  createItem,
  updateItem,
  deleteItem,
  columns,
  sortItems,
  entityLabel = 'item',
  getDeletePayload,
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await fetchItems();
      setIsLoading(false);
    };
    load();
  }, [fetchItems]);

  const data = useMemo(
    () => (sortItems ? sortItems(items || []) : items || []),
    [items, sortItems]
  );

  const openCreate = () => {
    setSelectedItem(null);
    setFormMode('create');
    setIsFormOpen(true);
  };

  const openEdit = (item) => {
    setSelectedItem(item);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const openDelete = (item) => {
    setSelectedItem(item);
    setIsDeleteModalOpen(true);
  };

  const closeModals = () => {
    setIsFormOpen(false);
    setIsDeleteModalOpen(false);
    setSelectedItem(null);
  };

  const handleCreate = async (formData) => {
    try {
      await createItem(formData);
      toast.success(`${entityLabel} created successfully`);
      closeModals();
    } catch (error) {
      console.error(`Failed to create ${entityLabel}:`, error);
      toast.error(`Failed to create ${entityLabel}. Please try again.`);
    }
  };

  const handleUpdate = async (id, formData) => {
    try {
      await updateItem(id, formData);
      toast.success(`${entityLabel} updated successfully`);
      closeModals();
    } catch (error) {
      console.error(`Failed to update ${entityLabel}:`, error);
      toast.error(`Failed to update ${entityLabel}. Please try again.`);
    }
  };

  const handleDelete = async (id, extra) => {
    try {
      await deleteItem(id, extra);
      toast.success(`${entityLabel} deleted successfully`);
      closeModals();
    } catch (error) {
      console.error(`Failed to delete ${entityLabel}:`, error);
      toast.error(error?.message || `Failed to delete ${entityLabel}. Please try again.`);
    }
  };

  const handleFormSubmit = async (formData) => {
    if (formMode === 'edit' && selectedItem) {
      await handleUpdate(selectedItem.id, formData);
    } else {
      await handleCreate(formData);
    }
  };

  const table = useReactTable({
    data,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const deletePayload = selectedItem
    ? getDeletePayload
      ? getDeletePayload(selectedItem)
      : { ...selectedItem, dataName: entityLabel.toLowerCase() }
    : { dataName: entityLabel.toLowerCase() };

  return {
    table,
    isLoading,
    globalFilter,
    setGlobalFilter,
    isFormOpen,
    formMode,
    isDeleteModalOpen,
    selectedItem,
    deletePayload,
    openCreate,
    openEdit,
    openDelete,
    closeModals,
    handleFormSubmit,
    handleDelete,
  };
};

export default useCrudListPage;
